import type {
  ClientCorporation,
  ClientCorporationEventLog,
  ClientCorporationMissionRecord,
  ClientCorporationRank,
  ClientUserCorporationJoinRequest,
  CompleteCorporationMissionRecord,
  CorporationMissionBoardItem,
} from '@game/shared-types';
import { create } from 'zustand';

import { getSortedInventory } from '@util/getSortedInventory';

import { corporationDefault, type CorporationState } from './corporationState';
import { persistentStore } from './persistentStore';
import { store } from './store';

interface Store extends CorporationState {
  setCorporation: (corporation: ClientCorporation) => void;
  resetState(): void;
  setJoinRequests: (joinRequestsMade: ClientUserCorporationJoinRequest[]) => void;
  addEventLog: (eventLog: ClientCorporationEventLog) => void;
  addEventLogs: (eventLogs: ClientCorporationEventLog[]) => void;
  setActiveMissions: (missions: ClientCorporationMissionRecord[]) => void;
  setCompleteMissions: (missions: ClientCorporationMissionRecord[]) => void;
  setCompleteMissionsRecords: (records: CompleteCorporationMissionRecord[]) => void;
  setMissionBoard: (missions: CorporationMissionBoardItem[]) => void;
}

export const corporationStore = create<Store>((set) => ({
  ...corporationDefault,
  resetState: (): void => {
    set(corporationDefault);
  },
  setJoinRequests: (joinRequestsMade: ClientUserCorporationJoinRequest[]): void => {
    set((state) => ({
      ...state,
      joinRequestsMade,
    }));
  },
  addEventLog: (eventLog: ClientCorporationEventLog): void => {
    set((state) => ({
      ...state,
      eventLogs: [eventLog, ...state.eventLogs],
    }));
  },
  addEventLogs: (eventLogs: ClientCorporationEventLog[]): void => {
    set((state) => ({
      ...state,
      eventLogs: [...eventLogs, ...state.eventLogs],
    }));
  },
  setActiveMissions: (missions: ClientCorporationMissionRecord[]): void => {
    set((state) => ({
      ...state,
      activeMissions: missions,
    }));
  },
  setCompleteMissions: (missions: ClientCorporationMissionRecord[]): void => {
    set((state) => ({
      ...state,
      completeMissions: missions,
    }));
  },
  setCompleteMissionsRecords: (records: CompleteCorporationMissionRecord[]): void => {
    set((state) => ({
      ...state,
      completeMissionsRecords: records,
    }));
  },
  setMissionBoard: (missions: CorporationMissionBoardItem[]): void => {
    set((state) => ({
      ...state,
      missionBoard: missions,
    }));
  },
  setCorporation: (corporation: ClientCorporation): void => {
    let rank: ClientCorporationRank | undefined;

    const inventoryState = persistentStore.getState().inventoryState;

    for (const member of corporation.members) {
      if (member.id === store.getState().id) {
        rank = corporation.ranks.find((r) => r.id === member.rankId);
        break;
      }
    }
    set((state) => ({
      ...state,
      ...corporation,
      rank,
      sortedInventory: getSortedInventory(corporation.inventory, inventoryState),
    }));
  },
}));
